import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/cementeries.actions';
import { cementeriesSelector } from 'redux/selectors/cementeries.selector';
import { history } from 'helpers/history';
import { COLORS } from 'helpers/enums/colors';

import LayoutContainer from 'containers/LayoutContainer';
import CementeriesView from 'views/CementeriesView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { ICementery } from 'types/cementeries.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/CementeriesView/i18n';

const CementeriesContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<ICementery | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.cementeries);
  const items = useTypedSelector(cementeriesSelector);

  const actions = [
    {
      label: i18next.t<string>('CEMENTERIES_VIEW:SHOW'),
      onClick: (id: string) => history.push(`/cementeries/${id}/blocks`),
    },
    {
      label: i18next.t<string>('CEMENTERIES_VIEW:EDIT'),
      onClick: (id: string) => history.push(`/cementeries/${id}/edit`),
    },
    {
      label: i18next.t<string>('CEMENTERIES_VIEW:DELETE'),
      onClick: (id: string) =>
        setDeleteItem(items.find((item: ICementery) => item.id === Number(id))),
      color: COLORS.redMonza,
    },
  ];

  const columns = [
    {
      name: 'name',
      label: i18next.t<string>('CEMENTERIES_VIEW:NAME'),
    },
    {
      name: 'id',
      label: i18next.t<string>('CEMENTERIES_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions} />,
        sort: false,
      },
    },
  ];

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer auth title={i18next.t<string>('CEMENTERIES_VIEW:TITLE')}>
      <CementeriesView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onAddClick={() => history.push('/cementeries/create')}
        onTableUpdate={onTableUpdate}
        table={table}
      />
      <Dialog
        message={i18next.t<string>('CEMENTERIES_VIEW:DELETE_DIALOG_MESSAGE', {
          name: deleteItem?.name,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t<string>('CEMENTERIES_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default CementeriesContainer;
